'use client';

import React, { useState } from 'react';

import { useTranslations, useLocale } from 'next-intl';
import styled, { css } from 'styled-components';

import { useShowOlderVersions } from './SelectedProductSlugsProvider';
import { getMetaTitle } from './utils';
import Button from '@/ui/components/Button';
import { addLocalePath } from '@/utils/urlBuilder/utils';

const ButtonWrap = styled.div<{ $center?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  ${({ $center = false }) => $center && 'justify-content: center;'}

  &:not(:first-child) {
    margin-top: 10px;
  }
`;

const TagButton = styled(Button)<{ active?: boolean }>`
  background-color: ${({ active }) => (active ? '#FFEEE8' : 'initial')};

  ${({ active }) =>
    active &&
    css`
      cursor: default;
      color: ${props => props.theme.palette.text.primary};

      &:hover {
        background-color: #ffeee8;
        color: ${props => props.theme.palette.text.primary};
      }
    `}
`;

interface DownloadFilterProps {
  products: Product[];
}

// Flattens the product tree so the small filter view can list all options.
const flattenProducts = (items: Product[]): Product[] => {
  const result: Product[] = [];

  const flatten = (arr: Product[]) => {
    arr.forEach(item => {
      result.push({ ...item, childrens: undefined });
      if (item.childrens && item.childrens.length > 0) {
        flatten(item.childrens);
      }
    });
  };

  flatten(items);

  return result;
};

// Returns the main set of products for the small view.
// (For family products, we return only the first child.)
const getMainFilterProducts = (data: Product[]): Product[] => {
  const result: Product[] = data.reduce((acc, parent) => {
    if (!parent?.slug.includes('family')) {
      acc.push(parent);
    } else if (parent.childrens && parent.childrens.length > 0) {
      const sortedChildren = parent.childrens.sort((a, b) => a.order - b.order);
      acc.push(sortedChildren[0]);
    }
    return acc;
  }, [] as Product[]);

  return result.sort((a, b) => a.order - b.order).slice(0, 9);
};

export default function DownloadFilter({ products }: DownloadFilterProps) {
  const locale = useLocale();
  const t = useTranslations();
  const { selectedProductSlugs, setSelectedProductSlugs } = useShowOlderVersions();
  const [showMore, setShowMore] = useState(false);

  // Helper function that updates the URL and the filter state.
  const updateFilter = (slug: string, additionalSlugs: string[] = []) => {
    window.history.pushState(null, '', addLocalePath(locale, `/downloads/${slug}`));
    setSelectedProductSlugs([slug, ...additionalSlugs]);

    // Find the selected product to get its title
    const selectedProduct = flattenedProducts.find(product => product?.slug === slug);

    document.title = getMetaTitle(t('downloads-title'), selectedProduct);
  };

  // For the small filter view, we include any child slugs, too.
  const handleItemClick = (item: Product) => () => {
    const additional = item.childrens ? item.childrens.map(child => child?.slug) : [];
    updateFilter(item?.slug, additional);
  };

  const mainFilterProducts = getMainFilterProducts(products);
  const flattenedProducts = flattenProducts(products);

  const handleClear = () => {
    setSelectedProductSlugs([]);
    window.history.pushState(null, '', addLocalePath(locale, '/downloads/'));

    // Reset the document title when clearing filters
    document.title = getMetaTitle(t('downloads-title'));
  };

  return (
    <>
      {selectedProductSlugs.length > 0 ? (
        <ButtonWrap $center>
          {selectedProductSlugs.length === 1 ? (
            // Small filter view
            flattenedProducts.map(item =>
              selectedProductSlugs.includes(item?.slug) ? (
                <ButtonWrap key={item?.slug}>
                  <TagButton
                    active={selectedProductSlugs.includes(item?.slug)}
                    label={item.title}
                    color="secondary"
                    variant={selectedProductSlugs.includes(item?.slug) ? undefined : 'outlined'}
                    onClick={handleItemClick(item)}
                  />
                </ButtonWrap>
              ) : null
            )
          ) : (
            // Expanded view: show complete tree.
            <>
              {products.map(item =>
                selectedProductSlugs.includes(item?.slug) ? (
                  <>
                    <div key={item?.slug} style={{ width: '100%' }}>
                      <ButtonWrap $center>
                        <TagButton
                          active={selectedProductSlugs.includes(item?.slug)}
                          label={item.title}
                          color="secondary"
                          variant={selectedProductSlugs.includes(item?.slug) ? undefined : 'outlined'}
                          onClick={handleItemClick(item)}
                        />
                      </ButtonWrap>
                    </div>
                    {item.childrens?.map(child => (
                      <Button
                        key={child?.slug}
                        label={child.title}
                        color="primary"
                        variant="outlined"
                        onClick={() => updateFilter(child?.slug)}
                      />
                    ))}
                  </>
                ) : null
              )}
            </>
          )}
          <Button label={t('downloads-filter-clear')} color="secondary" variant="outlined" onClick={handleClear} />
        </ButtonWrap>
      ) : !showMore ? (
        <ButtonWrap $center>
          {mainFilterProducts.map(item => (
            <Button
              key={item?.slug}
              label={item.title}
              color="secondary"
              variant="outlined"
              onClick={handleItemClick(item)}
            />
          ))}
          <Button label={t('downloads-filter-more')} variant="outlined" onClick={() => setShowMore(true)} />
        </ButtonWrap>
      ) : (
        // Expanded tree view.
        <>
          {products.map(item => (
            <ButtonWrap key={item?.slug}>
              {item.childrens && item.childrens.length > 0 ? (
                <>
                  {(!item?.slug.includes('family') || (item?.slug.includes('family') && item.childrens.length > 1)) && (
                    <Button
                      label={item.title.replace('family', t('downloads-filter-family'))}
                      color="secondary"
                      variant="outlined"
                      onClick={handleItemClick(item)}
                    />
                  )}
                  {item.childrens.map(child => (
                    <Button
                      key={child?.slug}
                      label={child.title}
                      color={item?.slug.includes('family') && item.childrens.length > 1 ? 'primary' : 'secondary'}
                      variant="outlined"
                      onClick={() => updateFilter(child?.slug)}
                    />
                  ))}
                </>
              ) : (
                <Button
                  label={item.title}
                  color="secondary"
                  variant="outlined"
                  onClick={() => updateFilter(item?.slug)}
                />
              )}
            </ButtonWrap>
          ))}
          <ButtonWrap $center>
            <Button
              label={t('downloads-filter-close')}
              color="secondary"
              variant="outlined"
              onClick={() => setShowMore(false)}
            />
          </ButtonWrap>
        </>
      )}
    </>
  );
}
